/**
 * Lungs organ texture — twin ribbed bellows with wooden yoke + central
 * coupling/gauge, olive belt straps with silver buckles wrapping over each
 * cylinder, and a white base panel with maintenance markings. Inspired by
 * the BrainPOP "Guts and Bolts" reference.
 *
 * 5 cells wide × 3 cells tall = 250 × 150 px.
 *
 * Port layout (ports themselves are external sprites; the texture just shows
 * the sockets/flanges where they meet the body):
 *   top    (col 2, dRow -1) — trachea, bidirectional, coneFlipped → central coupling
 *   left   (col -1, dRow 0) — CO₂ blood in → left trumpet flange at row-0 edge
 *   right  (col 5, dRow 0)  — O₂ blood out → right trumpet flange at row-0 edge
 *   bottom (col 2, dRow 3)  — diaphragm pulse, castellated → centered gray socket
 */
import Phaser from 'phaser';
import { GRID_SIZE } from '../../grid';
import type { HasTexture } from './core';

export function registerLungsTextures(scene: Phaser.Scene, has: HasTexture): void {
  if (!has('organ-lungs-idle')) makeLungs(scene, 'organ-lungs-idle', false);
  if (!has('organ-lungs-active')) makeLungs(scene, 'organ-lungs-active', true);
}

function makeLungs(scene: Phaser.Scene, key: string, active: boolean): void {
  const cols = 5;
  const rows = 3;
  const w = cols * GRID_SIZE; // 250
  const h = rows * GRID_SIZE; // 150
  const g = scene.add.graphics();

  // ----- Palette -----
  const woodBody = active ? 0xd0b888 : 0xb89868;
  const woodGrain = 0x6a4a20;
  const woodOutline = 0x3a2810;
  const strapBody = 0x6a7038;
  const strapHighlight = 0x8a9050;
  const strapOutline = 0x2a3010;
  const couplingBody = 0x4a5028;
  const couplingHighlight = 0x6a7040;
  const couplingOutline = 0x161a06;
  const endCapBody = 0xeeeae0;
  const endCapShadow = 0xb0aca0;
  const endCapOutline = 0x2a2a2a;
  const domeBody = 0x7a8048;
  const domeHighlight = 0x9aa060;
  const domeOutline = 0x2a3010;
  const bellowsBody = active ? 0xf0c4b4 : 0xe2b09c;
  const bellowsHighlight = 0xffd8c8;
  const bellowsRib = 0xb88068;
  const bellowsOutline = 0x5a3018;
  const baseBody = 0xeeeae0;
  const baseShadow = 0x9a968c;
  const baseOutline = 0x1f1f1f;
  const greenMarker = 0x3a8838;
  const socketBody = 0xa8a8a8;

  // Connection-cell center for the bottom castellated joint (col 2 of the
  // 5-wide organ — perfectly centered).
  const bottomSocketX = GRID_SIZE * 2.5; // 125

  // ===== 1. Drop shadow under the whole rig =====
  g.fillStyle(0x000000, 0.22);
  g.fillEllipse(w / 2, h - 2, w - 28, 12);

  // ===== 2. Bellows cylinders (twin stacks — the dominant body) =====
  const bellowsTop = 58;
  const bellowsBot = 130;
  const bellowsH = bellowsBot - bellowsTop;
  const bellowsW = 78;
  const ribCount = 7;
  const ribH = bellowsH / ribCount;
  // Two cylinders flanking the centered bottom socket, with a small gap between them.
  const leftCx = bottomSocketX - bellowsW / 2 - 6; // 80
  const rightCx = bottomSocketX + bellowsW / 2 + 6; // 170
  for (const stackCx of [leftCx, rightCx]) {
    const xL = stackCx - bellowsW / 2;
    const xR = stackCx + bellowsW / 2;
    // Dark outline
    g.fillStyle(bellowsOutline, 1);
    g.fillRect(xL - 1, bellowsTop - 1, bellowsW + 2, bellowsH + 2);
    // Body
    g.fillStyle(bellowsBody, 1);
    g.fillRect(xL, bellowsTop, bellowsW, bellowsH);
    // Per-rib: bright highlight band at top + dark shadow line at bottom
    for (let i = 0; i < ribCount; i++) {
      const ry0 = bellowsTop + i * ribH;
      const ry1 = ry0 + ribH;
      g.fillStyle(bellowsHighlight, 0.6);
      g.fillRect(xL + 1, ry0 + 1, bellowsW - 2, Math.max(2, ribH * 0.28));
      g.lineStyle(1.2, bellowsRib, 0.9);
      g.beginPath();
      g.moveTo(xL, ry1);
      g.lineTo(xR, ry1);
      g.strokePath();
    }
    // Subtle vertical edge shadows so the cylinders read as round
    g.fillStyle(bellowsRib, 0.35);
    g.fillRect(xL, bellowsTop, 4, bellowsH);
    g.fillRect(xR - 4, bellowsTop, 4, bellowsH);
  }

  // ===== 3. Olive-green domed shoulders (sit atop each bellows) =====
  const domeY = 52;
  for (const cx of [leftCx, rightCx]) {
    g.fillStyle(domeOutline, 1);
    g.fillEllipse(cx, domeY, bellowsW + 8, 28);
    g.fillStyle(domeBody, 1);
    g.fillEllipse(cx, domeY, bellowsW + 4, 24);
    g.fillStyle(domeHighlight, 0.55);
    g.fillEllipse(cx - 6, domeY - 4, bellowsW - 24, 10);
  }

  // ===== 4. White end-cap flanges at the side-port row =====
  const flangeMidY = GRID_SIZE * 0.5; // 25
  drawEndCap(g, 0, 22, flangeMidY, endCapBody, endCapShadow, endCapOutline, true);
  drawEndCap(g, w, w - 22, flangeMidY, endCapBody, endCapShadow, endCapOutline, false);

  // ===== 5. Wooden yoke bar across the top =====
  const yokeY = 18;
  const yokeH = 22;
  const yokeXL = 16;
  const yokeXR = w - 16;
  const yokeMidY = yokeY + yokeH / 2;
  g.fillStyle(woodOutline, 1);
  g.fillRoundedRect(yokeXL - 1, yokeY - 1, yokeXR - yokeXL + 2, yokeH + 2, 8);
  g.fillStyle(woodBody, 1);
  g.fillRoundedRect(yokeXL, yokeY, yokeXR - yokeXL, yokeH, 8);
  g.lineStyle(1, woodGrain, 0.45);
  for (const off of [-6, -2, 4]) {
    g.beginPath();
    g.moveTo(yokeXL + 6, yokeMidY + off);
    g.lineTo(yokeXR - 6, yokeMidY + off);
    g.strokePath();
  }
  g.fillStyle(0xffffff, 0.18);
  g.fillRoundedRect(yokeXL + 4, yokeY + 2, yokeXR - yokeXL - 8, 3, 2);

  // ===== 6. Belt straps from yoke down over each lung, with buckles =====
  // Each strap overlays the yoke (so it reads as wrapped around the bar) and
  // continues down across the dome onto the front of the cylinder, where a
  // silver belt buckle sits.
  const strapTopY = yokeY - 4; // starts just above the yoke (wrapping over it)
  const strapBotY = bellowsTop + 14; // ends on the front-upper portion of the cylinder
  for (const cx of [leftCx, rightCx]) {
    drawBeltStrap(g, cx, strapTopY, strapBotY, strapBody, strapHighlight, strapOutline);
  }
  // Buckles on the front of each cylinder, just below the dome
  for (const cx of [leftCx, rightCx]) {
    drawBuckle(g, cx, bellowsTop + 9);
  }

  // ===== 7. Central coupling block (trachea socket, holds the gauge) =====
  // Drawn after the straps so it sits in front, anchoring the center.
  const couplingW = 38;
  const couplingH = 46;
  const couplingX = w / 2 - couplingW / 2;
  const couplingY = 5;
  g.fillStyle(couplingOutline, 1);
  g.fillRoundedRect(couplingX - 1, couplingY - 1, couplingW + 2, couplingH + 2, 5);
  g.fillStyle(couplingBody, 1);
  g.fillRoundedRect(couplingX, couplingY, couplingW, couplingH, 5);
  g.fillStyle(couplingHighlight, 0.55);
  g.fillRoundedRect(couplingX + 2, couplingY + 2, couplingW - 4, 3, 2);
  // Gauge dial centered on the coupling
  drawGauge(g, w / 2, couplingY + couplingH * 0.52, 11, active);

  // ===== 8. White base panel (bottom shelf — the lungs sit on this) =====
  const baseY = bellowsBot - 1;
  const baseH = 17;
  const baseXL = 10;
  const baseXR = w - 10;
  g.fillStyle(baseOutline, 1);
  g.fillRect(baseXL - 1, baseY - 1, baseXR - baseXL + 2, baseH + 2);
  g.fillStyle(baseBody, 1);
  g.fillRect(baseXL, baseY, baseXR - baseXL, baseH);
  g.fillStyle(baseShadow, 0.7);
  g.fillRect(baseXL + 1, baseY + baseH - 2, baseXR - baseXL - 2, 1);
  g.fillStyle(0xffffff, 0.5);
  g.fillRect(baseXL + 1, baseY + 1, baseXR - baseXL - 2, 1);
  // Green marker scribble on the left side
  g.lineStyle(1.5, greenMarker, 0.85);
  g.beginPath();
  g.moveTo(baseXL + 8, baseY + 9);
  for (let i = 1; i <= 22; i++) {
    const t = i / 22;
    const x = baseXL + 8 + t * 78;
    const y = baseY + 9 + Math.sin(t * Math.PI * 3.4) * 2.5;
    g.lineTo(x, y);
  }
  g.strokePath();
  // Two red T-clamps on the right side
  drawClamp(g, w * 0.68, baseY + baseH / 2);
  drawClamp(g, w * 0.83, baseY + baseH / 2);

  // ===== 9. Small gray socket at bottom-center for the castellated joint =====
  const socketW = 20;
  const socketH = 5;
  g.fillStyle(endCapOutline, 1);
  g.fillRect(bottomSocketX - socketW / 2 - 1, h - socketH - 1, socketW + 2, socketH + 2);
  g.fillStyle(socketBody, 1);
  g.fillRect(bottomSocketX - socketW / 2, h - socketH, socketW, socketH);
  g.fillStyle(0xffffff, 0.4);
  g.fillRect(bottomSocketX - socketW / 2 + 1, h - socketH + 1, socketW - 2, 1);

  g.generateTexture(key, w, h);
  g.destroy();
}

/**
 * Trumpet-shaped white end cap protruding from the yoke at the side-port row.
 * The wide end faces outward (where the external port cone plugs in); narrow
 * end meets the yoke.
 */
function drawEndCap(
  g: Phaser.GameObjects.Graphics,
  outX: number,
  innerX: number,
  midY: number,
  body: number,
  shadow: number,
  outline: number,
  isLeft: boolean,
): void {
  const innerHalfH = 7;
  const outerHalfH = 13;
  const ix = innerX;
  const ox = outX;
  // Outline polygon (1px larger on all sides)
  g.fillStyle(outline, 1);
  g.beginPath();
  if (isLeft) {
    g.moveTo(ix + 1, midY - innerHalfH - 1);
    g.lineTo(ox - 1, midY - outerHalfH - 1);
    g.lineTo(ox - 1, midY + outerHalfH + 1);
    g.lineTo(ix + 1, midY + innerHalfH + 1);
  } else {
    g.moveTo(ix - 1, midY - innerHalfH - 1);
    g.lineTo(ox + 1, midY - outerHalfH - 1);
    g.lineTo(ox + 1, midY + outerHalfH + 1);
    g.lineTo(ix - 1, midY + innerHalfH + 1);
  }
  g.closePath();
  g.fillPath();
  // Body
  g.fillStyle(body, 1);
  g.beginPath();
  g.moveTo(ix, midY - innerHalfH);
  g.lineTo(ox, midY - outerHalfH);
  g.lineTo(ox, midY + outerHalfH);
  g.lineTo(ix, midY + innerHalfH);
  g.closePath();
  g.fillPath();
  // Shadow on the lower half
  g.fillStyle(shadow, 0.55);
  g.beginPath();
  g.moveTo(ix, midY + 1);
  g.lineTo(ox, midY + 1);
  g.lineTo(ox, midY + outerHalfH);
  g.lineTo(ix, midY + innerHalfH);
  g.closePath();
  g.fillPath();
  // Inner rim line (dark, just inside the outer edge)
  g.lineStyle(1, outline, 0.5);
  g.beginPath();
  g.moveTo(ox, midY - outerHalfH + 1);
  g.lineTo(ox, midY + outerHalfH - 1);
  g.strokePath();
}

/**
 * Vertical belt strap running from above the yoke down onto the cylinder.
 * Olive canvas color with stitching marks along both edges.
 */
function drawBeltStrap(
  g: Phaser.GameObjects.Graphics,
  cx: number,
  topY: number,
  botY: number,
  body: number,
  highlight: number,
  outline: number,
): void {
  const strapW = 16;
  const xL = cx - strapW / 2;
  const len = botY - topY;
  // Outline
  g.fillStyle(outline, 1);
  g.fillRect(xL - 1, topY - 1, strapW + 2, len + 2);
  // Body
  g.fillStyle(body, 1);
  g.fillRect(xL, topY, strapW, len);
  // Left highlight stripe
  g.fillStyle(highlight, 0.55);
  g.fillRect(xL + 2, topY, 2, len);
  // Stitching marks along both edges
  g.lineStyle(0.8, outline, 1);
  for (let dy = 4; dy < len - 2; dy += 5) {
    g.beginPath();
    g.moveTo(xL + 2, topY + dy);
    g.lineTo(xL + 4, topY + dy);
    g.moveTo(xL + strapW - 4, topY + dy);
    g.lineTo(xL + strapW - 2, topY + dy);
    g.strokePath();
  }
}

/** Silver belt buckle — rectangular frame with a vertical prong in the middle. */
function drawBuckle(g: Phaser.GameObjects.Graphics, cx: number, cy: number): void {
  const w = 24;
  const h = 14;
  const xL = cx - w / 2;
  const yT = cy - h / 2;
  // Drop shadow
  g.fillStyle(0x000000, 0.3);
  g.fillRect(xL + 1, yT + 2, w, h);
  // Dark frame
  g.fillStyle(0x1f1f1f, 1);
  g.fillRect(xL, yT, w, h);
  // Silver inner
  g.fillStyle(0xd4d4d4, 1);
  g.fillRect(xL + 1.5, yT + 1.5, w - 3, h - 3);
  // Top highlight
  g.fillStyle(0xffffff, 0.6);
  g.fillRect(xL + 1.5, yT + 1.5, w - 3, 2);
  // Bottom shadow
  g.fillStyle(0x6a6a6a, 0.7);
  g.fillRect(xL + 1.5, yT + h - 2.5, w - 3, 1);
  // Prong (vertical bar in middle)
  g.lineStyle(2, 0x1f1f1f, 1);
  g.beginPath();
  g.moveTo(cx, yT + 2);
  g.lineTo(cx, yT + h - 2);
  g.strokePath();
}

/** Small pressure dial — dark rim, off-white face, tick marks, red needle. */
function drawGauge(
  g: Phaser.GameObjects.Graphics,
  x: number,
  y: number,
  r: number,
  active: boolean,
): void {
  g.fillStyle(0x000000, 0.3);
  g.fillCircle(x + 0.5, y + 1, r);
  g.fillStyle(0x2a2a2a, 1);
  g.fillCircle(x, y, r);
  g.fillStyle(0xf0e8d8, 1);
  g.fillCircle(x, y, r - 1.5);
  g.lineStyle(0.9, 0x4a4a4a, 1);
  for (let i = 0; i < 8; i++) {
    const a = (i / 8) * Math.PI * 2 - Math.PI / 2;
    const r1 = r - 2.5;
    const r2 = r - 4;
    g.beginPath();
    g.moveTo(x + Math.cos(a) * r2, y + Math.sin(a) * r2);
    g.lineTo(x + Math.cos(a) * r1, y + Math.sin(a) * r1);
    g.strokePath();
  }
  const ang = active ? Math.PI * 0.25 : -Math.PI * 0.55;
  g.lineStyle(1.5, 0xb02020, 1);
  g.beginPath();
  g.moveTo(x, y);
  g.lineTo(x + Math.cos(ang) * (r - 3), y + Math.sin(ang) * (r - 3));
  g.strokePath();
  g.fillStyle(0x202020, 1);
  g.fillCircle(x, y, 1.5);
}

/**
 * Red T-clamp (wing nut / fastener) — small cross shape with drop shadow
 * and a bright highlight on top.
 */
function drawClamp(g: Phaser.GameObjects.Graphics, x: number, y: number): void {
  const body = 0xc02020;
  const dark = 0x6a0808;
  const hi = 0xff7060;
  g.fillStyle(0x000000, 0.3);
  g.fillRect(x - 6, y - 4, 12, 3);
  g.fillRect(x - 2, y - 4, 4, 9);
  g.fillStyle(body, 1);
  g.fillRect(x - 6, y - 5, 12, 4);
  g.fillRect(x - 2, y - 5, 4, 9);
  g.fillStyle(hi, 0.75);
  g.fillRect(x - 6, y - 5, 12, 1);
  g.fillRect(x - 2, y - 4, 4, 1);
  g.lineStyle(0.8, dark, 1);
  g.strokeRect(x - 6, y - 5, 12, 4);
  g.strokeRect(x - 2, y - 5, 4, 9);
}
